import { Injectable, inject } from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import type {
  BidBuyerProjectFeesCollection,
  QuotationFundRequest,
  QuotationFundRequestsCollection,
  QuotationRevision,
} from '@freelancer/datastore/collections';
import { ProjectFeeType } from '@freelancer/milestone-cart';
import type { PartialPaymentsCartItem } from '@freelancer/payments-cart';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextTypeApi } from 'api-typings/payments/payments';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

@UntilDestroy({ className: 'QuotationFundCartService' })
@Injectable({ providedIn: 'root' })
export class QuotationFundCartService {
  private datastore = inject(Datastore);

  createQuotationFundCartItem({
    quotation,
    amount,
  }: {
    quotation: QuotationRevision;
    amount: number;
  }): PartialPaymentsCartItem {
    return {
      contextType: ContextTypeApi.QUOTATION_FUND_PROACTIVE,
      contextId: `${quotation.quotationId}`,
      description: 'Quote Fund',
      currencyId: quotation.defaultCurrencyId,
      amount,
      useBonus: false,
    };
  }

  async createQuotationFundRequestCartItem({
    quotation,
    fundRequestId,
    currencyId,
  }: {
    quotation: QuotationRevision;
    fundRequestId: number;
    currencyId: number;
  }): Promise<PartialPaymentsCartItem> {
    const fundRequest: QuotationFundRequest = await firstValueFrom(
      this.datastore
        .collection<QuotationFundRequestsCollection>(
          'quotationFundRequests',
          query =>
            query
              .where('quotationId', '==', quotation.quotationId)
              .where('id', '==', fundRequestId),
        )
        .valueChanges()
        .pipe(
          map(q => q[0]),
          untilDestroyed(this),
        ),
    );

    return this.transformQuotationFundRequestCartItem(fundRequest, currencyId);
  }

  async createProjectFeeCartItem({
    bidId,
    currencyId,
    quoteId,
  }: {
    bidId: number;
    quoteId: number;
    currencyId: number;
  }): Promise<PartialPaymentsCartItem> {
    // On Award and fixed price project, charge the project fee based on bid amount.
    const projectFee = await firstValueFrom(
      this.datastore
        .document<BidBuyerProjectFeesCollection>('bidBuyerProjectFees', bidId)
        .valueChanges()
        .pipe(untilDestroyed(this)),
    );

    return this.transformProjectFeeToCartItem({
      quoteId,
      currencyId,
      fee: projectFee.amount,
      subType: ProjectFeeType.FIXED_PRICE_PROJECT,
    });
  }

  private transformQuotationFundRequestCartItem(
    fundRequest: QuotationFundRequest,
    currencyId: number,
  ): PartialPaymentsCartItem {
    return {
      contextType: ContextTypeApi.QUOTATION_FUND_REQUESTED,
      contextId: `${fundRequest.id}`,
      description: `Initial Payment for Quotation Fund Request (ID: ${fundRequest.id})`,
      currencyId,
      amount: fundRequest.amount,
    };
  }

  private transformProjectFeeToCartItem({
    quoteId,
    currencyId,
    fee,
    subType = ProjectFeeType.FIXED_PRICE_PROJECT,
  }: {
    quoteId: number;
    currencyId: number;
    fee: number;
    subType: ProjectFeeType;
  }): PartialPaymentsCartItem {
    return {
      contextType: ContextTypeApi.PROJECT_FEE,
      contextId: `${quoteId}`,
      description: 'Project Fee',
      currencyId,
      amount: fee,
      useBonus: false,
      contextSubType: subType,
    };
  }
}
